/**
 * Carries — an item carries part of the worth of the items it enables, with a
 * stated magnitude (canon 12 §5.11; owner's settled vocabulary, 2026-10-28).
 *
 * Stored as an `item_relationship_edges` row with `relationship_type = 'carries'`:
 * source carries target = the source carries part of the worth of the target it
 * enables. `magnitude` is that share as a fraction above 0 and at most 1
 * (0.4 = 40%). The database refuses a carries edge without it (migration 968).
 *
 * Together, the shares carried of one target add up to at most 1 (owner ruling
 * 2026-10-29; migration 999 refuses the write that would cross it, naming the
 * numbers). `carriesMagnitudeProblem` says the same words before the save.
 *
 * People type and read a percent; the record holds the fraction. Everything that
 * converts, validates or words a Carries edge goes through this file.
 */

import type { ItemRelationshipEdge, RelationshipEdgeType } from '../types/prioritization/itemRelationships';

export const CARRIES_DEFINITION =
  'An item carries part of the worth of the items it enables, and says how much.';

/**
 * What a reader may infer from a Carries edge, and what not (canon 12 §5.11).
 * Carries explains a rank. It never feeds a gate, the planning engine or the
 * capacity engine.
 */
export const CARRIES_READING = {
  mayInfer:
    "The source's rank is explained, in the stated share, by the worth of what it carries.",
  mayNotInfer: [
    'that one item depends on the other',
    'that either item must be selected, or selected with the other',
    'an order in which the work is done',
  ],
} as const;

/** The smallest and largest percent a person may type (stored as 0.0001 to 1). */
export const CARRIES_MIN_PERCENT = 0.01;
export const CARRIES_MAX_PERCENT = 100;

/** The shares carried of one item add up to at most this fraction (migration 999). */
export const CARRIES_CAP = 1;

/** A person's percent (40) as the stored fraction (0.4). Rounded to four places. */
export function percentToMagnitude(percent: number): number {
  return Math.round(percent * 100) / 10000;
}

/** The stored fraction (0.4) as the percent a person reads (40). */
export function magnitudeToPercent(magnitude: number): number {
  return Math.round(magnitude * 10000) / 100;
}

/** "40%", "12.5%". */
export function formatCarriesShare(magnitude: number): string {
  return `${magnitudeToPercent(magnitude)}%`;
}

/** A dismissed or dissolved edge carries nothing. */
function isLiveEdge(edge: Pick<ItemRelationshipEdge, 'status'>): boolean {
  return edge.status !== 'dismissed' && edge.status !== 'dissolved';
}

/**
 * How much of one item's worth is already carried: the shares of every live
 * Carries edge whose target is that item, added up (four places, as stored).
 * `exceptEdgeId` leaves one edge out, for a change to that edge's own share.
 */
export function carriedShareOf(
  targetItemId: string,
  edges: ReadonlyArray<Pick<ItemRelationshipEdge, 'id' | 'type' | 'targetItemId' | 'status' | 'magnitude'>>,
  exceptEdgeId?: string,
): number {
  const sum = edges.reduce(
    (total, e) =>
      e.type === 'carries' &&
      e.targetItemId === targetItemId &&
      e.id !== exceptEdgeId &&
      e.magnitude != null &&
      isLiveEdge(e)
        ? total + e.magnitude
        : total,
    0,
  );
  return Math.round(sum * 10000) / 10000;
}

/** What is already carried of the target, and its title, for the cap's words. */
export interface CarriedSoFar {
  alreadyCarried: number;
  targetTitle: string;
}

/** "60% of the worth of “X” is already carried; 40% is left." — read before the share is typed. */
export function carriedSoFarSentence({ alreadyCarried, targetTitle }: CarriedSoFar): string {
  if (alreadyCarried <= 0) return `None of the worth of “${targetTitle}” is carried yet.`;
  const left = Math.max(0, Math.round((CARRIES_CAP - alreadyCarried) * 10000) / 10000);
  return `${formatCarriesShare(alreadyCarried)} of the worth of “${targetTitle}” is already carried; ${formatCarriesShare(left)} is left.`;
}

/**
 * Why this type and magnitude cannot be saved together, or null when they can.
 * A carries edge needs a share above 0 and at most 100%; any other type has none.
 * With `soFar`, the shares carried of one target may not add up to more than
 * 100% — the same words, with the same numbers, as the table's refusal
 * (migration 999): what is already carried, what this would add, the total, the cap.
 */
export function carriesMagnitudeProblem(
  type: RelationshipEdgeType,
  magnitude: number | null | undefined,
  soFar?: CarriedSoFar,
): string | null {
  if (type !== 'carries') {
    return magnitude == null ? null : 'Only a Carries relationship states a share.';
  }
  if (magnitude == null || !Number.isFinite(magnitude)) {
    return 'Say how much of its worth this item carries, as a percent.';
  }
  if (magnitude <= 0 || magnitude > 1) {
    return 'The share is more than 0% and at most 100%.';
  }
  if (soFar) {
    const total = Math.round((soFar.alreadyCarried + magnitude) * 10000) / 10000;
    if (total > CARRIES_CAP) {
      return `${formatCarriesShare(soFar.alreadyCarried)} of the worth of “${soFar.targetTitle}” is already carried; this would add ${formatCarriesShare(magnitude)}, for ${formatCarriesShare(total)}. The shares carried of one item add up to at most 100%.`;
    }
  }
  return null;
}

/**
 * Reads what a person typed in the percent box. Returns the stored fraction, or
 * the problem in words — including the cap, when `soFar` says what the target
 * already has carried.
 */
export function parseCarriesPercent(
  input: string,
  soFar?: CarriedSoFar,
): { magnitude: number } | { problem: string } {
  const text = input.trim().replace(/%$/, '').trim();
  if (text === '') return { problem: 'Say how much of its worth this item carries, as a percent.' };
  const percent = Number(text);
  if (!Number.isFinite(percent)) return { problem: 'The share is a number, such as 40.' };
  if (percent < CARRIES_MIN_PERCENT || percent > CARRIES_MAX_PERCENT) {
    return { problem: 'The share is more than 0% and at most 100%.' };
  }
  const magnitude = percentToMagnitude(percent);
  const problem = carriesMagnitudeProblem('carries', magnitude, soFar);
  return problem ? { problem } : { magnitude };
}

export function isCarriesEdge(edge: Pick<ItemRelationshipEdge, 'type'>): boolean {
  return edge.type === 'carries';
}

/**
 * How a Carries edge reads from one item's page: the carrier reads
 * "Carries 40% of <target>", the carried item reads "<source> carries 40% of this".
 * Returns the words around the other item's title so a screen can link it.
 */
export function carriesPhrase(
  edge: Pick<ItemRelationshipEdge, 'sourceItemId' | 'magnitude'>,
  viewItemId: string,
): { before: string; after: string } {
  const share = edge.magnitude != null ? formatCarriesShare(edge.magnitude) : 'an unstated share';
  return edge.sourceItemId === viewItemId
    ? { before: `Carries ${share} of `, after: '' }
    : { before: '', after: ` carries ${share} of this` };
}

/** The same reading as one sentence, for a timeline note or a plain-text list. */
export function carriesSentence(
  edge: Pick<ItemRelationshipEdge, 'sourceItemId' | 'magnitude'>,
  viewItemId: string,
  otherTitle: string,
): string {
  const { before, after } = carriesPhrase(edge, viewItemId);
  return `${before}"${otherTitle}"${after}`;
}
